import { CouponCollectionCreate } from "@saleor/coupons/components/CouponCollectionCreate";
import { useCouponCollectionCreate } from "@saleor/coupons/mutations";
import { CouponCollectionCreate as CouponCollectionData } from "@saleor/coupons/types/CouponCollectionCreate";
import {
  couponCollectionsPath,
  couponCollectionViewPath
} from "@saleor/coupons/url";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  CouponCollectionClassificationEnum,
  CouponCollectionStatusEnum
} from "@saleor/types/globalTypes";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import {
  CouponCollectionErrorForm,
  CouponCollectionFormData,
  couponCollectionPublishValidation,
  hasNoError
} from "@saleor/utils/validators/couponCollection";
import { couponCollectionDraftValidation } from "@saleor/utils/validators/couponCollection";
import Moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

export const CouponCollectionCreatePage: React.FC = () => {
  const [validator, setValidator] = useState<CouponCollectionErrorForm>({
    title: null
  });
  const [showChip, setShowChip] = useState(false);
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [
    couponCollectionImage,
    setCouponCollectionImageImageUpload
  ] = React.useState(null);

  const handleSuccess = (data: CouponCollectionData) => {
    if (data?.couponCollectionCreate?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Coupon Collection Created Successfully!"
        })
      });
      if (data?.couponCollectionCreate?.couponCollection?.status === "DRAFT") {
        setShowChip(true);
        setTimeout(() => {
          navigate(couponCollectionsPath);
        }, 3000);
      } else {
        navigate(
          couponCollectionViewPath(
            data?.couponCollectionCreate?.couponCollection?.id
          )
        );
      }
    } else if (data?.couponCollectionCreate?.errors) {
      notify({
        status: "error",
        text: data.couponCollectionCreate.errors[0].message
      });
    }
  };

  const [couponCollectionCreate] = useCouponCollectionCreate({
    onCompleted: handleSuccess
  });

  const handleSubmit = async (
    formData: CouponCollectionFormData,
    status: CouponCollectionStatusEnum
  ) => {
    const result = await couponCollectionCreate({
      variables: {
        input: {
          bannerImage: couponCollectionImage,
          categories: formData.categories,
          classification: CouponCollectionClassificationEnum.ALL,
          collectionType: formData.collectionType,
          endDate: changeTimeZone(formData.endDate, formData.endTime),
          isFeatured: formData.isFeatured,
          startDate: changeTimeZone(formData.startDate, formData.startTime),
          status,
          title: formData.title,
          writeup: JSON.stringify(formData.writeup),
          slug: formData.slug || null
        }
      }
    });
    if (result && result?.data?.couponCollectionCreate) {
      return result?.data?.couponCollectionCreate?.errors;
    }
    return null;
  };

  const onDraft = async (formData: CouponCollectionFormData) => {
    const validateDraft = couponCollectionDraftValidation(formData);
    setValidator(validateDraft);
    if (!hasNoError(validateDraft)) {
      return;
    }
    handleSubmit(formData, CouponCollectionStatusEnum.DRAFT);
  };

  const onPublish = async (formData: CouponCollectionFormData) => {
    const dateNow = new Date().toLocaleString() + "";
    const validateCreate = couponCollectionPublishValidation(formData);
    setValidator(validateCreate);
    if (!hasNoError(validateCreate)) {
      if (validateCreate.bannerImage) {
        notify({
          status: "error",
          text: validateCreate.bannerImage
        });
      }
      return;
    }
    if (formData.startDate === Moment(dateNow).format("YYYY-MM-DD")) {
      await handleSubmit(formData, CouponCollectionStatusEnum.ACTIVE);
    } else {
      await handleSubmit(formData, CouponCollectionStatusEnum.SCHEDULED);
    }
  };

  const [, dispatchAppState] = useAppState();
  useEffect(() => {
    dispatchAppState({
      payload: {
        value: false
      },
      type: "displayLoader"
    });
  }, []);

  return (
    <CouponCollectionCreate
      showChip={showChip}
      onDraft={onDraft}
      onPublish={onPublish}
      onBack={() => navigate("/coupons/collection")}
      errors={validator}
      onCouponCollectionImageUpload={setCouponCollectionImageImageUpload}
      couponCollectionImage={couponCollectionImage}
    />
  );
};

export default CouponCollectionCreatePage;
